import {base64ToBlob, saveOnCdnFiles} from "@/utilities/ImageGallery";

export class CanvasHandler {
  constructor(canvasId) {
    this.canvas = document.getElementById(canvasId);
    this.ctx = this.canvas.getContext('2d');

    this.image = new Image();
    this.image.crossOrigin = "anonymous";
    this.backgroundImage = new Image();

    this.bgMode = 'image'; // 'image', 'color', 'gradient'
    this.bgColor = '#ffffff';
    this.bgGradient = {type: 'linear', colors: ['#000', '#fff']};

    this.state = {
      x: 100,
      y: 100,
      width: 200,
      height: 200,
      dragging: false,
      resizing: false,
      hovered: false,
      resizeHandleSize: 10,
      selectedHandle: null,
      rotation: 0,
      rotating: false,
      rotationHandleOffset: 40,
    };

    this.offsetX = 0;
    this.offsetY = 0;

    this.loadingOverlay = document.getElementById('loadingOverlay');

    this.initEvents();
    this.resizeCanvas();
  }

  resizeCanvas() {
    this.loadingOverlay = document.getElementById('loadingOverlay');
    const canvas = this.canvas = document.getElementById('canvas')
    const ctx = this.ctx = this.canvas.getContext('2d')

    const wrapper = canvas.parentElement;
    const width = wrapper.clientWidth;
    const height = wrapper.clientHeight;

    // const dpr = window.devicePixelRatio || 1;
    const dpr = 1;

    this.canvas.width = width * dpr;
    this.canvas.height = height * dpr;
    // this.canvas.style.width = width + 'px';
    this.canvas.style.height = height + 'px';

    ctx.setTransform(1, 0, 0, 1, 0, 0); // reset transform
    ctx.scale(dpr, dpr);

    this.initEvents()
    this.draw(); // redraw everything at new size
  }

  loadImage(src) {
    this.image.src = src;
    this.showLoading(true);
    this.image.onload = () => {
      const canvasWidth = this.canvas.width;
      const canvasHeight = this.canvas.height;

      const imgRatio = this.image.width / this.image.height;
      const canvasRatio = canvasWidth / canvasHeight;

      if (imgRatio > canvasRatio) {
        this.state.width = Math.min(this.image.width, canvasWidth * 0.9);
        this.state.height = this.state.width / imgRatio;
      } else {
        this.state.height = Math.min(this.image.height, canvasHeight * 0.9);
        this.state.width = this.state.height * imgRatio;
      }

      this.state.x = (canvasWidth - this.state.width) / 2;
      this.state.y = (canvasHeight - this.state.height) / 2;

      this.showLoading(false)

      this.state.hovered = true
      this.draw();
    };
    this.image.onerror = () => {
      this.showLoading(false);
      console.error("Failed to load image.");
    };
  }

  setBackground(src) {
    this.bgMode = 'image';
    this.showLoading(true);
    const bg = new Image();
    bg.crossOrigin = 'anonymous';
    bg.src = src;
    bg.onload = () => {
      this.backgroundImage = bg;
      this.showLoading(false);
      this.draw();
    };
    bg.onerror = () => {
      this.showLoading(false);
      console.error("Failed to load background image.");
    };
  }

  showLoading(show) {
    if (this.loadingOverlay) {
      this.loadingOverlay.style.display = show ? 'flex' : 'none';
    }
  }

  setBackgroundFromFile(file) {
    this.bgMode = 'image';
    this.showLoading(true);
    const reader = new FileReader();
    reader.onload = (e) => this.setBackground(e.target.result);
    reader.readAsDataURL(file);
  }

  setBackgroundColor(color) {
    this.bgMode = 'color';
    this.bgColor = color;
    this.draw();
  }

  setBackgroundGradient(type, colors) {
    this.bgMode = 'gradient';
    this.bgGradient = {type, colors};
    this.draw();
  }

  setRotation(degrees) {
    this.state.rotation = degrees;
    this.draw();
  }

  initEvents() {
    this.canvas.addEventListener('mousemove', this.onMouseMove.bind(this));
    this.canvas.addEventListener('mousedown', this.onMouseDown.bind(this));
    this.canvas.addEventListener('mouseup', this.onMouseUp.bind(this));
  }

  onMouseMove(e) {
    const rect = this.canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const isInImage =
        mouseX >= this.state.x &&
        mouseX <= this.state.x + this.state.width &&
        mouseY >= this.state.y &&
        mouseY <= this.state.y + this.state.height;

    const handle = this.getHandleUnderMouse(mouseX, mouseY);
    const onRotation = this.getRotationHandleHit(mouseX, mouseY);

    if (handle) {
      this.canvas.style.cursor = this.getCursorForHandle(handle);
    } else if (onRotation) {
      this.canvas.style.cursor = 'grab';
    } else if (isInImage) {
      this.canvas.style.cursor = 'move';
    } else {
      this.canvas.style.cursor = 'default';
    }

    // this.state.hovered = isInImage || handle || onRotation;

    if (this.state.resizing && this.state.selectedHandle) {
      this.resizeImage(mouseX, mouseY);
    } else if (this.state.dragging) {
      this.state.x = mouseX - this.offsetX;
      this.state.y = mouseY - this.offsetY;
    } else if (this.state.rotating) {
      const centerX = this.state.x + this.state.width / 2;
      const centerY = this.state.y + this.state.height / 2;
      const dx = mouseX - centerX;
      const dy = mouseY - centerY;
      const angle = Math.atan2(dy, dx);
      this.state.rotation = (angle * 180) / Math.PI + 90;
    }

    this.draw();
  }

  onMouseDown(e) {
    const rect = this.canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const handle = this.getHandleUnderMouse(mouseX, mouseY);
    if (this.getRotationHandleHit(mouseX, mouseY)) {
      this.state.rotating = true;
    } else if (handle) {
      this.state.resizing = true;
      this.state.selectedHandle = handle;
    } else if (
        mouseX >= this.state.x &&
        mouseX <= this.state.x + this.state.width &&
        mouseY >= this.state.y &&
        mouseY <= this.state.y + this.state.height
    ) {
      this.state.dragging = true;
      this.offsetX = mouseX - this.state.x;
      this.offsetY = mouseY - this.state.y;
    }
  }

  onMouseUp() {
    this.state.dragging = false;
    this.state.resizing = false;
    this.state.rotating = false;
    this.state.selectedHandle = null;
  }

  draw() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    if (this.backgroundImage.complete) {
      this.drawBackground(this.canvas.width, this.canvas.height);
    }

    this.ctx.save();
    const cx = this.state.x + this.state.width / 2;
    const cy = this.state.y + this.state.height / 2;
    this.ctx.translate(cx, cy);
    this.ctx.rotate((this.state.rotation * Math.PI) / 180);

    try {
      this.ctx.drawImage(
          this.image,
          -this.state.width / 2,
          -this.state.height / 2,
          this.state.width,
          this.state.height
      )
    } catch (e) {
      console.log(e)
    }

    this.ctx.restore();

    // if (this.state.hovered || this.state.dragging || this.state.resizing || this.state.rotating)
    this.state.hovered && this.drawTransformBorder();
  }

  drawBackground(canvasWidth, canvasHeight) {
    if (this.bgMode === 'image' && this.backgroundImage.complete) {
      const img = this.backgroundImage;
      const imgRatio = img.width / img.height;
      const canvasRatio = canvasWidth / canvasHeight;

      let drawWidth, drawHeight, offsetX, offsetY;

      if (imgRatio > canvasRatio) {
        drawHeight = canvasHeight;
        drawWidth = drawHeight * imgRatio;
        offsetX = (canvasWidth - drawWidth) / 2;
        offsetY = 0;
      } else {
        drawWidth = canvasWidth;
        drawHeight = drawWidth / imgRatio;
        offsetX = 0;
        offsetY = (canvasHeight - drawHeight) / 2;
      }

      this.ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
    } else if (this.bgMode === 'color') {
      this.ctx.fillStyle = this.bgColor;
      this.ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    } else if (this.bgMode === 'gradient') {
      let gradient;
      if (this.bgGradient.type === 'linear') {
        gradient = this.ctx.createLinearGradient(0, 0, canvasWidth, canvasHeight);
      } else {
        gradient = this.ctx.createRadialGradient(
            canvasWidth / 2, canvasHeight / 2, 0,
            canvasWidth / 2, canvasHeight / 2, canvasWidth / 1.5
        );
      }

      const stops = this.bgGradient.colors.length;
      this.bgGradient.colors.forEach((color, i) => {
        gradient.addColorStop(i / (stops - 1), color);
      });

      this.ctx.fillStyle = gradient;
      this.ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    }
  }

  drawTransformBorder() {
    this.ctx.strokeStyle = '#04f1f1';
    this.ctx.lineWidth = 1;
    this.ctx.setLineDash([5, 3]);
    this.ctx.strokeRect(this.state.x, this.state.y, this.state.width, this.state.height);
    this.ctx.setLineDash([]);

    // Corner handles
    const handles = this.getResizeHandles();
    this.ctx.fillStyle = '#1b95e5';
    for (let handle of handles) {
      this.ctx.fillRect(handle.x, handle.y, handle.size, handle.size);
    }

    // Rotation handle
    const cx = this.state.x + this.state.width / 2;
    const cy = this.state.y - this.state.rotationHandleOffset;

    this.ctx.beginPath();
    this.ctx.moveTo(this.state.x + this.state.width / 2, this.state.y);
    this.ctx.lineTo(cx, cy);
    this.ctx.stroke();

    this.ctx.beginPath();
    this.ctx.arc(cx, cy, 6, 0, Math.PI * 2);
    this.ctx.fillStyle = '#0f0';
    this.ctx.fill();
    this.ctx.stroke();
  }

  getResizeHandles() {
    const s = this.state.resizeHandleSize;
    return [
      {name: 'br', x: this.state.x + this.state.width - s / 2, y: this.state.y + this.state.height - s / 2, size: s},
      {name: 'tr', x: this.state.x + this.state.width - s / 2, y: this.state.y - s / 2, size: s},
      {name: 'bl', x: this.state.x - s / 2, y: this.state.y + this.state.height - s / 2, size: s},
      {name: 'tl', x: this.state.x - s / 2, y: this.state.y - s / 2, size: s},
    ];
  }

  getCursorForHandle(handle) {
    switch (handle) {
      case 'br':
        return 'nwse-resize';
      case 'tr':
        return 'nesw-resize';
      case 'bl':
        return 'nesw-resize';
      case 'tl':
        return 'nwse-resize';
      default:
        return 'default';
    }
  }

  getHandleUnderMouse(x, y) {
    for (let handle of this.getResizeHandles()) {
      if (x >= handle.x && x <= handle.x + handle.size &&
          y >= handle.y && y <= handle.y + handle.size) {
        return handle.name;
      }
    }
    return null;
  }

  getRotationHandleHit(x, y) {
    const cx = this.state.x + this.state.width / 2;
    const cy = this.state.y - this.state.rotationHandleOffset;
    const dx = x - cx;
    const dy = y - cy;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance <= 8;
  }

  resizeImage(mouseX, mouseY) {
    switch (this.state.selectedHandle) {
      case 'br':
        this.state.width = mouseX - this.state.x;
        this.state.height = mouseY - this.state.y;
        break;
      case 'tr':
        this.state.height += this.state.y - mouseY;
        this.state.y = mouseY;
        this.state.width = mouseX - this.state.x;
        break;
      case 'bl':
        this.state.width += this.state.x - mouseX;
        this.state.x = mouseX;
        this.state.height = mouseY - this.state.y;
        break;
      case 'tl':
        this.state.width += this.state.x - mouseX;
        this.state.height += this.state.y - mouseY;
        this.state.x = mouseX;
        this.state.y = mouseY;
        break;
    }
  }

  downloadImage(filename = 'canvas.png', format = 'image/png') {
    this.state.hovered = false
    this.draw()
    const link = document.createElement('a');
    link.download = filename;
    link.href = this.canvas.toDataURL(format);
    link.click();
    this.state.hovered = true
    this.draw()
  }

  saveImage(filename = 'canvas.png', format = 'image/png') {
    this.state.hovered = false
    this.draw()

    this.showLoading(true)

    // Convert canvas content to a data URL (PNG format)
    const imageDataUrl = this.canvas.toDataURL(format);

    // Example Base64 string and mimeType
    const base64String = imageDataUrl.replace("data:image/png;base64,", "");
    const mimeType = "application/octet-stream"; // Adjust the MIME type as needed

    // Convert Base64 to Blob
    const fileBlob = base64ToBlob(base64String, mimeType);

    return new Promise((resolve, reject) => {
      saveOnCdnFiles(
          fileBlob,
          filename.replace(/ /g, "_"),
          "تصاویر محصول"
      )
          .then(({data}) => {
            // resolve(data.data.link)
            resolve(data)
          })
          .catch((err) => {
            reject(err)
            console.log(err)
          })
          .finally(() => {
            this.showLoading(false)
            this.state.hovered = true
            this.draw()
          })
    })
  }
}